import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Alert, Badge, Nav, Modal, Row, Col, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { calculateSeminarScore, getEvaluatorName, countBimbinganSessions } from '../utils/seminarUtils';
import { generateLogbookPDF } from '../utils/pdfGenerator';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  doc as firestoreDoc,
  getDoc,
  addDoc,
  updateDoc,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link, useNavigate } from 'react-router-dom';
import { FaUser, FaClipboardList, FaArrowRight, FaInfoCircle, FaUserCheck, FaCalendarAlt, FaFileDownload } from 'react-icons/fa';

const MahasiswaBimbingan = () => {
  const { currentUser, userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mahasiswaList, setMahasiswaList] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [showSeminarForm, setShowSeminarForm] = useState(false);
  const [selectedMahasiswa, setSelectedMahasiswa] = useState(null);
  const [selectedJenisSeminar, setSelectedJenisSeminar] = useState('');
  const [pendingRescheduleRequests, setPendingRescheduleRequests] = useState([]);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedRescheduleRequest, setSelectedRescheduleRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [pendingTitleChangeRequests, setPendingTitleChangeRequests] = useState([]);
  const [showTitleChangeModal, setShowTitleChangeModal] = useState(false);
  const [selectedTitleChangeRequest, setSelectedTitleChangeRequest] = useState(null);
  const [showTitleChangeRejectConfirm, setShowTitleChangeRejectConfirm] = useState(false);
  const [titleChangeRejectReason, setTitleChangeRejectReason] = useState('');
  const [loadingLogbook, setLoadingLogbook] = useState(null); // Track which mahasiswa logbook is being generated
  const navigate = useNavigate();

  useEffect(() => {
    let isActive = true;

    if (userRole !== 'dosen') {
      setError("Halaman ini hanya untuk dosen");
      setLoading(false);
      return;
    }

    const fetchAllData = async () => {
      try {
        if (!isActive) return;

        await Promise.all([
          fetchMahasiswaBimbingan(),
          checkPendingAssignments(),
          fetchPendingRescheduleRequests(),
          fetchPendingTitleChangeRequests()
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (isActive) {
          setError("Terjadi kesalahan saat mengambil data. Silakan coba lagi.");
        }
      }
    };

    fetchAllData();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isActive = false;
    };
  }, [currentUser, userRole]);

  // Fetch pending reschedule requests that need P1 approval
  const fetchPendingRescheduleRequests = async () => {
    try {
      // Get all pending reschedule requests where this dosen is P1
      const rescheduleQuery = query(
        collection(db, "usulanSeminar"),
        where("pembimbing1Id", "==", currentUser.uid),
        where("status", "==", "pending_p1_approval")
      );

      const rescheduleSnapshot = await getDocs(rescheduleQuery);

      const requestsData = [];
      for (const doc of rescheduleSnapshot.docs) {
        const requestData = {
          id: doc.id,
          ...doc.data()
        };

        // Always fetch complete student data to ensure we have current information
        if (requestData.mahasiswaId) {
          try {
            const userDocRef = firestoreDoc(db, "users", requestData.mahasiswaId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              requestData.mahasiswaNama = userData.nama || 'Mahasiswa';
              requestData.mahasiswaNIM = userData.nim || '-';
              requestData.mahasiswaEmail = userData.email;
              requestData.mahasiswaProdi = userData.prodi;
            }
          } catch (error) {
            console.error("Error fetching mahasiswa data:", error);
          }
        }

        // Get previous seminar to show score details
        if (requestData.previousSeminarId) {
          try {
            const seminarDocRef = firestoreDoc(db, "seminar", requestData.previousSeminarId);
            const seminarDoc = await getDoc(seminarDocRef);
            if (seminarDoc.exists()) {
              const seminarData = seminarDoc.data();
              requestData.previousSeminar = seminarData;

              // Fetch evaluator details if not already in the data
              if (seminarData.pengujiId && seminarData.pengujiId.length > 0) {
                const evaluatorDetails = [];

                // Get details for each evaluator
                for (let i = 0; i < seminarData.pengujiId.length; i++) {
                  const pengujiId = seminarData.pengujiId[i];
                  try {
                    // Skip if we don't have a valid ID
                    if (!pengujiId) continue;

                    const pengujiDocRef = firestoreDoc(db, "users", pengujiId);
                    const pengujiDoc = await getDoc(pengujiDocRef);

                    if (pengujiDoc.exists()) {
                      const pengujiData = pengujiDoc.data();
                      evaluatorDetails.push({
                        id: pengujiId,
                        nama: pengujiData.nama || seminarData.pengujiNama?.[i] || `Penguji ${i + 1}`,
                        role: i === 0 ? "Pembimbing 1" :
                          i === 1 ? "Pembimbing 2" :
                            `Penguji ${i - 1}`,
                        weight: i === 0 ? 0.35 :
                          i === 1 ? 0.25 :
                            i === 2 ? 0.20 : 0.20
                      });
                    }
                  } catch (error) {
                    console.error(`Error fetching evaluator ${pengujiId} data:`, error);
                  }
                }

                // Attach evaluator details to the seminar data
                requestData.previousSeminar.evaluatorDetails = evaluatorDetails;

                // Calculate final score
                if (seminarData.nilai) {
                  const scores = [];
                  const pengujiNilai = seminarData.nilai;
                  const kriteriaPenilaian = seminarData.kriteriaPenilaian || [];

                  // Calculate individual scores
                  for (const evaluator of evaluatorDetails) {
                    if (pengujiNilai[evaluator.id]) {
                      let total = 0;
                      let count = 0;
                      const criteriaScores = [];

                      kriteriaPenilaian.forEach(kriteria => {
                        if (kriteria && kriteria.id && pengujiNilai[evaluator.id][kriteria.id]) {
                          const nilai = pengujiNilai[evaluator.id][kriteria.id];
                          const bobot = kriteria.bobot / 100;
                          const weightedScore = nilai * bobot;

                          total += weightedScore;
                          count++;

                          criteriaScores.push({
                            name: kriteria.nama,
                            weight: kriteria.bobot,
                            score: nilai,
                            weighted: weightedScore
                          });
                        }
                      });

                      scores.push({
                        evaluatorId: evaluator.id,
                        evaluatorName: evaluator.nama,
                        role: evaluator.role,
                        weight: evaluator.weight,
                        score: count > 0 ? total : 0,
                        criteriaScores: criteriaScores
                      });
                    }
                  }

                  // Calculate final weighted score
                  let finalScore = 0;
                  let totalWeight = 0;

                  scores.forEach(score => {
                    finalScore += score.score * score.weight;
                    totalWeight += score.weight;
                  });

                  // Normalize in case not all evaluators submitted scores
                  if (totalWeight > 0 && totalWeight < 1) {
                    finalScore = finalScore / totalWeight;
                  }

                  requestData.previousSeminar.calculatedScores = scores;
                  requestData.previousSeminar.finalScore = finalScore;
                  requestData.previousSeminar.isPassed = finalScore >= 60;
                }
              }
            }
          } catch (error) {
            console.error("Error fetching previous seminar:", error);
          }
        }

        requestsData.push(requestData);
      }

      setPendingRescheduleRequests(requestsData);
    } catch (error) {
      console.error("Error fetching reschedule requests:", error);
    }
  };

  // Fetch pending title change requests that need P1 approval
  const fetchPendingTitleChangeRequests = async () => {
    try {
      // Get all tugas akhir where this dosen is P1 and there's a pending title change request
      const taQuery = query(
        collection(db, "tugasAkhir"),
        where("pembimbingId", "array-contains", currentUser.uid),
        where("status", "==", "approved")
      );

      const taSnapshot = await getDocs(taQuery);
      const requestsData = [];

      for (const taDoc of taSnapshot.docs) {
        const taData = taDoc.data();

        // Check if this is P1 (first in pembimbingId array)
        if (taData.pembimbingId && Array.isArray(taData.pembimbingId) && taData.pembimbingId[0] === currentUser.uid) {
          // Check if there's a pending title change request
          if (taData.usulanPerubahan && taData.usulanPerubahan.status === 'pending_p1_approval') {
            // Get mahasiswa data
            let mahasiswaData = null;
            if (taData.mahasiswaId) {
              try {
                const userDocRef = firestoreDoc(db, "users", taData.mahasiswaId);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                  mahasiswaData = userDoc.data();
                }
              } catch (error) {
                console.error("Error fetching mahasiswa data:", error);
              }
            }

            requestsData.push({
              id: taDoc.id,
              ...taData,
              mahasiswa: mahasiswaData
            });
          }
        }
      }

      setPendingTitleChangeRequests(requestsData);
    } catch (error) {
      console.error("Error fetching title change requests:", error);
    }
  };

  // Handle approve title change request - langsung update tanpa ke admin
  const handleApproveTitleChange = async () => {
    if (!selectedTitleChangeRequest) return;

    try {
      setLoading(true);

      const ta = selectedTitleChangeRequest;
      const usulan = ta.usulanPerubahan;
      const judulBaru = usulan.judul;
      const judulLama = ta.judul;
      const driveLinkBaru = usulan.driveLink || ta.driveLink;

      const taRef = firestoreDoc(db, "tugasAkhir", ta.id);

      // 1. Update judul dan driveLink di dokumen tugas akhir
      const updatedUsulanPerubahan = {
        ...usulan,
        status: 'approved',
        tanggal_approve: new Date().toISOString(),
        approvedBy: currentUser.uid,
        pembimbing1Approval: {
          approved: true,
          timestamp: new Date().toISOString(),
          pembimbingId: currentUser.uid,
          pembimbingNama: currentUser.displayName || 'Dosen'
        }
      };

      await updateDoc(taRef, {
        judul: judulBaru,
        driveLink: driveLinkBaru,
        usulanPerubahan: updatedUsulanPerubahan
      });

      // 2. Update semua referensi judul di bimbingan
      const bimbinganQuery = query(
        collection(db, "bimbingan"),
        where("tugasAkhirId", "==", ta.id)
      );
      const bimbinganSnapshot = await getDocs(bimbinganQuery);

      const bimbinganUpdates = [];
      bimbinganSnapshot.forEach((bimbinganDoc) => {
        const bimbinganData = bimbinganDoc.data();
        const updateData = {};

        // Update judul bimbingan
        if (bimbinganData.judul) {
          let updatedJudul = bimbinganData.judul;
          let needUpdate = false;

          // Case 1: Format "Pengantar Bimbingan - [JUDUL]"
          if (updatedJudul.startsWith('Pengantar Bimbingan - ')) {
            updatedJudul = `Pengantar Bimbingan - ${judulBaru}`;
            needUpdate = true;
          }
          // Case 2: Jika judul sama persis dengan judul lama
          else if (updatedJudul === judulLama) {
            updatedJudul = judulBaru;
            needUpdate = true;
          }
          // Case 3: Jika judul mengandung judul lama
          else if (updatedJudul.includes(judulLama)) {
            const escapedJudulLama = judulLama.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            updatedJudul = updatedJudul.replace(new RegExp(escapedJudulLama, 'g'), judulBaru);
            needUpdate = true;
          }

          if (needUpdate && updatedJudul !== bimbinganData.judul) {
            updateData.judul = updatedJudul;
          }
        }

        // Update catatan jika mengandung judul lama
        if (bimbinganData.catatan && bimbinganData.catatan.includes(judulLama)) {
          updateData.catatan = bimbinganData.catatan.replace(
            new RegExp(judulLama.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            judulBaru
          );
        }

        if (Object.keys(updateData).length > 0) {
          bimbinganUpdates.push(updateDoc(doc(db, "bimbingan", bimbinganDoc.id), updateData));
        }
      });

      // 3. Update semua referensi judul di seminar
      const seminarQuery = query(
        collection(db, "seminar"),
        where("tugasAkhirId", "==", ta.id)
      );
      const seminarSnapshot = await getDocs(seminarQuery);

      const seminarUpdates = [];
      seminarSnapshot.forEach((seminarDoc) => {
        const seminarData = seminarDoc.data();
        const updateData = {};

        // Update judulTA jika berbeda dengan judul baru
        if (seminarData.judulTA && seminarData.judulTA !== judulBaru) {
          updateData.judulTA = judulBaru;
        }

        if (Object.keys(updateData).length > 0) {
          seminarUpdates.push(updateDoc(doc(db, "seminar", seminarDoc.id), updateData));
        }
      });

      // 4. Eksekusi semua update secara parallel
      await Promise.all([...bimbinganUpdates, ...seminarUpdates]);

      setSuccess(`Permintaan perubahan judul telah disetujui. Judul telah diupdate dari "${judulLama}" menjadi "${judulBaru}".`);
      setShowTitleChangeModal(false);

      // Refresh data
      fetchPendingTitleChangeRequests();
      fetchMahasiswaBimbingan();
    } catch (error) {
      console.error("Error approving title change request:", error);
      setError("Terjadi kesalahan saat menyetujui permintaan: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle reject title change request
  const handleRejectTitleChange = async () => {
    if (!selectedTitleChangeRequest) return;

    try {
      setLoading(true);

      const taRef = firestoreDoc(db, "tugasAkhir", selectedTitleChangeRequest.id);

      // Update usulan perubahan status to rejected
      await updateDoc(taRef, {
        'usulanPerubahan.status': 'rejected',
        'usulanPerubahan.pembimbing1Approval': {
          approved: false,
          timestamp: new Date().toISOString(),
          pembimbingId: currentUser.uid,
          pembimbingNama: currentUser.displayName || 'Dosen',
          reason: titleChangeRejectReason || null
        },
        'usulanPerubahan.alasan': titleChangeRejectReason || 'Ditolak oleh Pembimbing 1',
        'usulanPerubahan.statusMessage': titleChangeRejectReason ?
          `Ditolak oleh Pembimbing 1: ${titleChangeRejectReason}` :
          'Ditolak oleh Pembimbing 1'
      });

      setSuccess(`Permintaan perubahan judul telah ditolak.`);
      setShowTitleChangeModal(false);
      setShowTitleChangeRejectConfirm(false);
      setTitleChangeRejectReason('');

      // Refresh data
      fetchPendingTitleChangeRequests();
      fetchMahasiswaBimbingan();
    } catch (error) {
      console.error("Error rejecting title change request:", error);
      setError("Terjadi kesalahan saat menolak permintaan");
    } finally {
      setLoading(false);
    }
  };

  // Show title change request details
  const handleShowTitleChangeRequest = (request) => {
    setSelectedTitleChangeRequest(request);
    setTitleChangeRejectReason('');
    setShowTitleChangeModal(true);
  };

  // Debug function to log bimbingan counts
  const logBimbinganCounts = (bimbinganData, mahasiswaName) => {
    // Count valid sessions
    const validSessions = bimbinganData.filter(b => b.deleted !== true);
    const reviewed = validSessions.filter(b => b.status === 'reviewed');

    // Count by types
    const proposal = reviewed.filter(b => !b.jenisBimbingan || b.jenisBimbingan === 'proposal').length;
    const hasil = reviewed.filter(b => b.jenisBimbingan === 'hasil').length;
    const finalisasi = reviewed.filter(b => b.jenisBimbingan === 'finalisasi').length;

    console.log(`DEBUG COUNT - ${mahasiswaName || 'Unknown'}: 
      Total valid: ${validSessions.length}
      Reviewed: ${reviewed.length}
      Proposal: ${proposal}
      Hasil: ${hasil}
      Finalisasi: ${finalisasi}
      Ready for Proposal: ${proposal >= 5}
      Ready for Hasil: ${hasil >= 5}
      Ready for Sidang: ${finalisasi >= 2}
    `);

    return {
      total: validSessions.length,
      reviewed: reviewed.length,
      proposal,
      hasil,
      finalisasi
    };
  };

  const fetchMahasiswaBimbingan = async () => {
    try {
      setLoading(true);
      setError('');

      console.log("Current user ID in fetchMahasiswaBimbingan:", currentUser?.uid);

      // Get tugas akhir where this dosen is assigned as pembimbing
      // and has been accepted or doesn't have acceptance status yet
      const taQuery = query(
        collection(db, "tugasAkhir"),
        where("pembimbingId", "array-contains", currentUser.uid),
        where("status", "==", "approved")
      );

      console.log("Querying tugasAkhir with array-contains for:", currentUser?.uid);

      const taSnapshot = await getDocs(taQuery);
      const mahasiswaData = [];

      for (const taDoc of taSnapshot.docs) {
        const taData = taDoc.data();
        taData.id = taDoc.id;

        // Log the raw data for debugging
        console.log("TA Document:", taDoc.id);
        console.log("pembimbingId from DB:", taData.pembimbingId);

        // Ensure pembimbingId is always an array
        if (!Array.isArray(taData.pembimbingId)) {
          console.log("Converting pembimbingId to array:", taData.pembimbingId);

          if (typeof taData.pembimbingId === 'string') {
            taData.pembimbingId = [taData.pembimbingId];
          } else if (typeof taData.pembimbingId === 'object') {
            taData.pembimbingId = Object.values(taData.pembimbingId);
          } else {
            taData.pembimbingId = [];
          }

          console.log("Converted pembimbingId:", taData.pembimbingId);
        }

        // Check if this assignment has been rejected by the dosen
        let isRejected = false;

        if (taData.dosenAccept && taData.dosenAccept[currentUser.uid]) {
          const acceptRecord = taData.dosenAccept[currentUser.uid];
          if (acceptRecord.status === 'rejected') isRejected = true;
        }

        // Skip rejected assignments
        if (isRejected) continue;

        // Get mahasiswa data
        if (taData.mahasiswaId) {
          try {
            const mahasiswaDocRef = firestoreDoc(db, "users", taData.mahasiswaId);
            const mahasiswaDoc = await getDoc(mahasiswaDocRef);
            if (mahasiswaDoc.exists()) {
              taData.mahasiswa = mahasiswaDoc.data();
            }
          } catch (error) {
            console.error(`Error fetching mahasiswa data for ${taData.id}:`, error);
          }
        }

        // Set acceptance status
        if (taData.dosenAccept && taData.dosenAccept[currentUser.uid]) {
          const acceptRecord = taData.dosenAccept[currentUser.uid];
          taData.acceptStatus = acceptRecord.status;
        } else {
          taData.acceptStatus = 'pending';
        }

        // Get completed seminar information
        try {
          const completedSeminarsQuery = query(
            collection(db, "seminar"),
            where("tugasAkhirId", "==", taData.id),
            where("status", "==", "completed")
          );

          const completedSeminarsSnapshot = await getDocs(completedSeminarsQuery);

          // Track which types of seminars have been completed and passed
          taData.completedSeminars = {
            proposal: null,
            hasil: null,
            kompre: null
          };

          // Populate with seminar data
          completedSeminarsSnapshot.forEach(doc => {
            const seminarData = doc.data();
            seminarData.id = doc.id;

            // Check if seminar is passed (score >= 60)
            let isPassed = false;
            if (seminarData.nilai) {
              const pengujiNilai = seminarData.nilai;
              const kriteriaPenilaian = seminarData.kriteriaPenilaian || [];
              const standardWeights = [0.3, 0.2, 0.3, 0.2];

              // Get scores for each penguji
              if (Object.keys(pengujiNilai).length === 4) { // Only check if all scores are in
                const scores = Object.values(pengujiNilai).map(pengujiScore => {
                  let total = 0;
                  let count = 0;

                  kriteriaPenilaian.forEach(kriteria => {
                    if (kriteria && kriteria.id && pengujiScore[kriteria.id]) {
                      total += pengujiScore[kriteria.id] * (kriteria.bobot / 100);
                      count++;
                    }
                  });

                  return count > 0 ? total : 0;
                });

                // Calculate final score
                let finalScore = 0;
                scores.forEach((score, index) => {
                  const weight = index < standardWeights.length ? standardWeights[index] : 0.25;
                  finalScore += score * weight;
                });

                // Check if passed (score >= 60)
                isPassed = finalScore >= 60;
                seminarData.finalScore = finalScore;
              }
            }

            seminarData.isPassed = isPassed;
            taData.completedSeminars[seminarData.jenis] = seminarData;
          });
        } catch (error) {
          console.error(`Error fetching completed seminars for ${taData.id}:`, error);
        }

        // Get scheduled seminar information (to check if seminar is already scheduled)
        try {
          const scheduledSeminarsQuery = query(
            collection(db, "seminar"),
            where("tugasAkhirId", "==", taData.id),
            where("status", "==", "scheduled")
          );

          const scheduledSeminarsSnapshot = await getDocs(scheduledSeminarsQuery);

          // Track which types of seminars have been scheduled
          taData.scheduledSeminars = {
            proposal: null,
            hasil: null,
            kompre: null
          };

          // Populate with scheduled seminar data
          scheduledSeminarsSnapshot.forEach(doc => {
            const seminarData = doc.data();
            seminarData.id = doc.id;
            taData.scheduledSeminars[seminarData.jenis] = seminarData;
          });
        } catch (error) {
          console.error(`Error fetching scheduled seminars for ${taData.id}:`, error);
          taData.scheduledSeminars = {
            proposal: null,
            hasil: null,
            kompre: null
          };
        }

        // Get bimbingan count for this TA
        try {
          // Get all bimbingan for this TA (we'll filter deleted ones in memory)
          const bimbinganQuery = query(
            collection(db, "bimbingan"),
            where("tugasAkhirId", "==", taData.id),
            where("pembimbingId", "==", currentUser.uid)
          );
          const bimbinganSnapshot = await getDocs(bimbinganQuery);

          // Array to store all active sessions
          const bimbinganData = [];

          // Process each session from the snapshot
          bimbinganSnapshot.forEach(doc => {
            const data = doc.data();
            const docId = doc.id;

            // Apply multiple checks
            const isDeleted = data.deleted === true;
            const hasValidStatus = data.status === 'reviewed' || data.status === 'pending';
            const hasValidPembimbing = data.pembimbingId === currentUser.uid;
            const hasValidTugasAkhir = data.tugasAkhirId === taData.id;

            // Only include sessions that pass all validation checks
            if (!isDeleted && hasValidStatus && hasValidPembimbing && hasValidTugasAkhir) {
              bimbinganData.push({
                id: docId,
                ...data
              });
            }
          });

          // Count initial reviewed and pending sessions
          const initialReviewedCount = bimbinganData.filter(b => b.status === 'reviewed').length;
          const initialPendingCount = bimbinganData.filter(b => b.status === 'pending').length;

          // Perform efficient deduplication using Maps
          const uniqueById = new Map();

          bimbinganData.forEach(bimbingan => {
            if (uniqueById.has(bimbingan.id)) {
              // Keep the most recent version based on createdAt timestamp
              const existing = uniqueById.get(bimbingan.id);
              const existingDate = existing.createdAt ? new Date(existing.createdAt) : new Date(0);
              const newDate = bimbingan.createdAt ? new Date(bimbingan.createdAt) : new Date(0);

              if (newDate > existingDate) {
                uniqueById.set(bimbingan.id, bimbingan);
              }
            } else {
              uniqueById.set(bimbingan.id, bimbingan);
            }
          });

          // Convert Map back to array
          const dedupedById = Array.from(uniqueById.values());

          // Check for "logical" duplicates by creating a unique signature for each bimbingan
          // This signature will be based on the combination of pembimbing, jenis, and sesi
          const dedupeSignatureToId = new Map();
          const finalBimbingan = [];

          // Make a final pass to ensure we catch ALL forms of duplicates
          dedupedById.forEach(bimbingan => {
            // Create a signature that uniquely identifies this bimbingan session
            const signature = `${bimbingan.pembimbingId}|${bimbingan.jenisBimbingan || 'proposal'}|${bimbingan.sesi}`;

            if (dedupeSignatureToId.has(signature)) {
              // Handle same sesi for same pembimbing and jenis
              const existingId = dedupeSignatureToId.get(signature);
              const existing = finalBimbingan.find(b => b.id === existingId);

              // CRITICAL FIX: Ensure deleted sessions are properly handled
              // Priority: 1. Non-deleted over deleted 2. Most recent if both same deletion status
              if (existing.deleted === true && bimbingan.deleted !== true) {
                // Remove existing and add current
                const existingIndex = finalBimbingan.findIndex(b => b.id === existingId);
                finalBimbingan.splice(existingIndex, 1);
                finalBimbingan.push(bimbingan);
                dedupeSignatureToId.set(signature, bimbingan.id);
              } else if (existing.deleted !== true && bimbingan.deleted === true) {
                // Keep existing
              } else {
                // Both are either deleted or not, keep the most recent one
                const existingDate = existing.createdAt ? new Date(existing.createdAt) : new Date(0);
                const newDate = bimbingan.createdAt ? new Date(bimbingan.createdAt) : new Date(0);

                if (newDate > existingDate) {
                  // Remove existing and add current
                  const existingIndex = finalBimbingan.findIndex(b => b.id === existingId);
                  finalBimbingan.splice(existingIndex, 1);
                  finalBimbingan.push(bimbingan);
                  dedupeSignatureToId.set(signature, bimbingan.id);
                }
              }
            } else {
              dedupeSignatureToId.set(signature, bimbingan.id);
              finalBimbingan.push(bimbingan);
            }
          });

          // FINAL SAFETY CHECK: Ensure there are no deleted sessions in the final list
          const trulyFinalBimbingan = finalBimbingan.filter(b => b.deleted !== true);

          // Debug log to verify actual counts
          logBimbinganCounts(trulyFinalBimbingan, taData.mahasiswa?.nama);

          // Use the shared utility for consistent counting across the application
          const bimbinganCounts = countBimbinganSessions(trulyFinalBimbingan);

          // Update the taData with counts from the utility
          taData.totalBimbinganCount = bimbinganCounts.total;
          taData.bimbinganCount = bimbinganCounts.reviewed;
          taData.pendingCount = bimbinganCounts.pending;

          // Category counts based on reviewed sessions only
          taData.proposal = bimbinganCounts.proposal;
          taData.hasil = bimbinganCounts.hasil;
          taData.finalisasi = bimbinganCounts.finalisasi;

          // Check if ready for seminar/sidang - Make sure we properly set these flags
          taData.proposal = taData.proposal || 0;
          taData.hasil = taData.hasil || 0;
          taData.finalisasi = taData.finalisasi || 0;

          // Ensure readyFor flags are set correctly
          taData.readyForProposal = taData.proposal >= 5;
          taData.readyForHasil = taData.hasil >= 5;
          taData.readyForSidang = taData.finalisasi >= 2;

          // Debug log for values
          console.log(`Mahasiswa: ${taData.mahasiswa?.nama}, Proposal: ${taData.proposal}, Ready: ${taData.readyForProposal}`);
        } catch (error) {
          console.error(`Error fetching bimbingan count for ${taData.id}:`, error);
          taData.bimbinganCount = 0;
          taData.proposal = 0;
          taData.hasil = 0;
          taData.finalisasi = 0;
        }

        mahasiswaData.push(taData);
      }

      setMahasiswaList(mahasiswaData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching mahasiswa bimbingan:", error);
      setError("Terjadi kesalahan saat mengambil data mahasiswa bimbingan");
      setLoading(false);
    }
  };

  const checkPendingAssignments = async () => {
    try {
      // Get tugas akhir where this dosen is assigned as pembimbing
      const taQuery = query(
        collection(db, "tugasAkhir"),
        where("pembimbingId", "array-contains", currentUser.uid),
        where("status", "==", "approved")
      );

      const taSnapshot = await getDocs(taQuery);
      let pendingCount = 0;

      for (const taDoc of taSnapshot.docs) {
        const taData = taDoc.data();

        // Check if this assignment has been accepted or rejected by the dosen
        let isAccepted = false;
        let isRejected = false;

        if (taData.dosenAccept) {
          // If there's an acceptance record for this dosen
          const acceptRecord = taData.dosenAccept[currentUser.uid];
          if (acceptRecord) {
            if (acceptRecord.status === 'accepted') isAccepted = true;
            if (acceptRecord.status === 'rejected') isRejected = true;
          }
        }

        // Count if not accepted and not rejected
        if (!isAccepted && !isRejected) {
          pendingCount++;
        }
      }

      setPendingCount(pendingCount);
    } catch (error) {
      console.error("Error checking pending assignments:", error);
    }
  };

  const handleViewBimbingan = (mahasiswa) => {
    // Set localStorage to help filter bimbingan for this specific student
    localStorage.setItem('currentBimbinganMahasiswaId', mahasiswa.mahasiswaId);
    localStorage.setItem('currentBimbinganTugasAkhirId', mahasiswa.id);

    // Navigate to bimbingan page
    navigate('/bimbingan');
  };

  // Generate logbook PDF for a student
  const handleGenerateLogbook = async (mahasiswa) => {
    try {
      setLoadingLogbook(mahasiswa.id);
      setError('');

      // 1. Fetch all bimbingan for this TA from this dosen
      const bimbinganQuery = query(
        collection(db, "bimbingan"),
        where("tugasAkhirId", "==", mahasiswa.id),
        where("pembimbingId", "==", currentUser.uid)
      );
      const bimbinganSnapshot = await getDocs(bimbinganQuery);

      const bimbinganList = [];
      bimbinganSnapshot.forEach(doc => {
        const data = doc.data();
        // Only include non-deleted bimbingan
        if (data.deleted !== true) {
          bimbinganList.push({
            id: doc.id,
            ...data
          });
        }
      });

      console.log(`Found ${bimbinganList.length} bimbingan sessions for logbook`);

      // 2. Get dosen signature from current user's profile
      let dosenData = {
        nama: currentUser.displayName || 'Dosen Pembimbing',
        signatureURL: null
      };

      try {
        const dosenDoc = await getDoc(firestoreDoc(db, "users", currentUser.uid));
        if (dosenDoc.exists()) {
          const userData = dosenDoc.data();
          dosenData = {
            nama: userData.nama || currentUser.displayName || 'Dosen Pembimbing',
            nip: userData.nip || '-',
            signatureURL: userData.signatureURL || null
          };
        }
      } catch (error) {
        console.error("Error fetching dosen data:", error);
      }

      // 3. Prepare tugasAkhir data
      const tugasAkhirData = {
        id: mahasiswa.id,
        judul: mahasiswa.judul,
        prodi: mahasiswa.mahasiswa?.prodi
      };

      // 4. Prepare mahasiswa data
      const mahasiswaData = {
        nama: mahasiswa.mahasiswa?.nama || 'Mahasiswa',
        nim: mahasiswa.mahasiswa?.nim || '-',
        prodi: mahasiswa.mahasiswa?.prodi || 'REKAYASA KOSMETIK'
      };

      // 5. Generate PDF
      const pdfBlob = await generateLogbookPDF(tugasAkhirData, mahasiswaData, bimbinganList, dosenData);

      // 6. Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Logbook_Bimbingan_${mahasiswaData.nim}_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Logbook berhasil di-generate dan didownload.');
    } catch (error) {
      console.error("Error generating logbook:", error);
      setError("Terjadi kesalahan saat generate logbook: " + error.message);
    } finally {
      setLoadingLogbook(null);
    }
  };

  // Cek apakah dosen ini adalah pembimbing 1 (P1)
  const isPembimbing1 = (mahasiswa) => {
    // Add debug logging
    console.log("Checking isPembimbing1 for:", mahasiswa?.mahasiswa?.nama);
    console.log("Current user ID:", currentUser?.uid);
    console.log("PembimbingId structure:", mahasiswa?.pembimbingId);

    // Always return true for testing - REMOVE THIS IN PRODUCTION
    return true;

    // Extra safety checks
    if (!mahasiswa) {
      console.log("No mahasiswa data");
      return false;
    }

    if (!mahasiswa.pembimbingId) {
      console.log("No pembimbingId found");
      return false;
    }

    if (!currentUser || !currentUser.uid) {
      console.log("No currentUser data");
      return false;
    }

    // Different ways pembimbingId might be structured
    // Case 1: Array form [p1Id, p2Id]
    if (Array.isArray(mahasiswa.pembimbingId)) {
      const isP1 = mahasiswa.pembimbingId[0] === currentUser.uid;
      console.log("Array comparison:", isP1);
      return isP1;
    }

    // Case 2: Object form {0: p1Id, 1: p2Id}
    if (typeof mahasiswa.pembimbingId === 'object' && !Array.isArray(mahasiswa.pembimbingId)) {
      const isP1 = mahasiswa.pembimbingId[0] === currentUser.uid;
      console.log("Object comparison:", isP1);
      return isP1;
    }

    // Case 3: String form (single pembimbing)
    if (typeof mahasiswa.pembimbingId === 'string') {
      const isP1 = mahasiswa.pembimbingId === currentUser.uid;
      console.log("String comparison:", isP1);
      return isP1;
    }

    console.log("Unknown pembimbingId structure:", typeof mahasiswa.pembimbingId);
    return false;
  };

  // Ajukan seminar
  const handleAjukanSeminar = (mahasiswa, jenis) => {
    setSelectedMahasiswa(mahasiswa);
    setSelectedJenisSeminar(jenis);

    // Check if this is a reschedule (seminar exists but didn't pass)
    const isReschedule = mahasiswa.completedSeminars &&
      mahasiswa.completedSeminars[jenis] &&
      !mahasiswa.completedSeminars[jenis].isPassed;

    // Pass reschedule info
    if (isReschedule) {
      mahasiswa.isReschedule = true;
      mahasiswa.previousSeminar = mahasiswa.completedSeminars[jenis];
    } else {
      mahasiswa.isReschedule = false;
      mahasiswa.previousSeminar = null;
    }

    setShowSeminarForm(true);
  };

  // Show reschedule request details
  const handleShowRescheduleRequest = (request) => {
    setSelectedRescheduleRequest(request);
    setRejectReason(''); // Reset reject reason
    setShowRescheduleModal(true);
  };

  // Show approval confirmation
  const showApprovalConfirmation = () => {
    setShowApproveConfirm(true);
  };

  // Close approval confirmation
  const closeApprovalConfirmation = () => {
    setShowApproveConfirm(false);
  };

  // Handle approve reschedule request
  const handleApproveReschedule = async () => {
    if (!selectedRescheduleRequest) return;

    try {
      setLoading(true);

      // Update request status
      const rescheduleRef = firestoreDoc(db, "usulanSeminar", selectedRescheduleRequest.id);
      await updateDoc(rescheduleRef, {
        status: 'pending_admin_approval',
        statusMessage: "Disetujui oleh pembimbing, menunggu persetujuan Admin",
        pembimbing1Approval: {
          approved: true,
          timestamp: new Date().toISOString(),
          pembimbingId: currentUser.uid
        }
      });

      setSuccess(`Permintaan penjadwalan ulang seminar ${selectedRescheduleRequest.jenis} telah disetujui dan diteruskan ke Admin.`);
      setShowRescheduleModal(false);
      setShowApproveConfirm(false);

      // Refresh data
      fetchPendingRescheduleRequests();
    } catch (error) {
      console.error("Error approving reschedule request:", error);
      setError("Terjadi kesalahan saat menyetujui permintaan");
    } finally {
      setLoading(false);
    }
  };

  // Show rejection confirmation
  const showRejectConfirmation = () => {
    setShowRejectConfirm(true);
  };

  // Close rejection confirmation
  const closeRejectConfirmation = () => {
    setShowRejectConfirm(false);
  };

  // Handle reject reschedule request
  const handleRejectReschedule = async () => {
    if (!selectedRescheduleRequest) return;

    try {
      setLoading(true);

      // Update request status to rejected
      const rescheduleRef = firestoreDoc(db, "usulanSeminar", selectedRescheduleRequest.id);
      await updateDoc(rescheduleRef, {
        status: 'rejected',
        statusMessage: rejectReason ? `Ditolak oleh pembimbing: ${rejectReason}` : "Ditolak oleh pembimbing",
        pembimbing1Approval: {
          approved: false,
          timestamp: new Date().toISOString(),
          pembimbingId: currentUser.uid,
          reason: rejectReason || null
        }
      });

      setSuccess(`Permintaan penjadwalan ulang seminar ${selectedRescheduleRequest.jenis} telah ditolak.`);
      setShowRescheduleModal(false);
      setShowRejectConfirm(false);
      setRejectReason('');

      // Refresh data
      fetchPendingRescheduleRequests();
    } catch (error) {
      console.error("Error rejecting reschedule request:", error);
      setError("Terjadi kesalahan saat menolak permintaan");
    } finally {
      setLoading(false);
    }
  };

  // Submit usulan seminar
  const handleSubmitUsulanSeminar = async () => {
    if (!selectedMahasiswa || !selectedJenisSeminar) return;

    try {
      setLoading(true);

      // Check if there's already a pending request for this mahasiswa and seminar type
      const existingRequestQuery = query(
        collection(db, "usulanSeminar"),
        where("mahasiswaId", "==", selectedMahasiswa.mahasiswaId),
        where("jenis", "==", selectedJenisSeminar),
        where("status", "in", ["pending_approval", "pending_p1_approval", "pending_admin_approval"])
      );

      const existingRequestSnapshot = await getDocs(existingRequestQuery);
      if (!existingRequestSnapshot.empty) {
        setError(`Sudah ada usulan ${selectedJenisSeminar === 'proposal' ? 'Seminar Proposal' :
          selectedJenisSeminar === 'hasil' ? 'Seminar Hasil' :
            'Sidang Komprehensif'} yang menunggu persetujuan untuk mahasiswa ini.`);
        setLoading(false);
        return;
      }

      // Validate seminar progression requirements
      if (selectedJenisSeminar !== 'proposal') {
        // Check if previous seminars in the sequence are completed and passed
        const prerequisiteSeminar = selectedJenisSeminar === 'hasil' ? 'proposal' :
          selectedJenisSeminar === 'kompre' ? 'hasil' : null;

        if (prerequisiteSeminar) {
          // Check if the prerequisite seminar exists in completedSeminars
          const hasPassedPrerequisite = selectedMahasiswa.completedSeminars &&
            selectedMahasiswa.completedSeminars[prerequisiteSeminar] &&
            selectedMahasiswa.completedSeminars[prerequisiteSeminar].isPassed;

          if (!hasPassedPrerequisite) {
            const prerequisiteName = prerequisiteSeminar === 'proposal' ? 'Seminar Proposal' :
              prerequisiteSeminar === 'hasil' ? 'Seminar Hasil' :
                'Sidang Komprehensif';

            setError(`Mahasiswa harus LULUS ${prerequisiteName} terlebih dahulu sebelum dapat mengajukan ${selectedJenisSeminar === 'hasil' ? 'Seminar Hasil' : 'Sidang Komprehensif'}.`);
            setLoading(false);
            return;
          }
        }
      }

      // Check if there's already a completed seminar of this type
      const existingCompletedQuery = query(
        collection(db, "seminar"),
        where("tugasAkhirId", "==", selectedMahasiswa.id),
        where("jenis", "==", selectedJenisSeminar),
        where("status", "==", "completed"),
        orderBy("completedAt", "desc")
      );

      const existingCompletedSnapshot = await getDocs(existingCompletedQuery);

      if (!existingCompletedSnapshot.empty) {
        // A completed seminar exists - get the most recent one (first after sorting by completedAt desc)
        const existingSeminar = existingCompletedSnapshot.docs[0].data();

        // Only allow rescheduling if there's a failing grade (< 60)
        if (existingSeminar.nilai) {
          // Calculate if the seminar was passed using standardized method
          const result = calculateSeminarScore(existingSeminar);
          const finalScore = result.finalScore;

          // Check if passed (score >= 60)
          if (result.isPassed) {
            setError(`Mahasiswa ini sudah LULUS ${selectedJenisSeminar === 'proposal' ? 'Seminar Proposal' :
              selectedJenisSeminar === 'hasil' ? 'Seminar Hasil' :
                'Sidang Komprehensif'} dengan nilai ${finalScore.toFixed(2)}. Tidak perlu dijadwalkan ulang.`);
            setLoading(false);
            return;
          } else {
            // Failed but already has an assessment - this is a reschedule
            const usulanData = {
              jenis: selectedJenisSeminar,
              tugasAkhirId: selectedMahasiswa.id,
              mahasiswaId: selectedMahasiswa.mahasiswaId,
              mahasiswaNama: selectedMahasiswa.mahasiswa?.nama || 'Mahasiswa',
              mahasiswaNIM: selectedMahasiswa.mahasiswa?.nim || '-',
              judulTA: selectedMahasiswa.judul || 'Tugas Akhir',
              diajukanOleh: {
                dosen: currentUser.uid,
                nama: currentUser.displayName || 'Dosen',
                timestamp: new Date().toISOString(),
                reason: "Penjadwalan ulang - tidak lulus seminar sebelumnya (nilai < 60)"
              },
              status: 'pending_approval', // langsung ke admin karena diajukan oleh P1
              isReschedule: true,
              previousSeminarId: existingCompletedSnapshot.docs[0].id,
              createdAt: new Date().toISOString()
            };

            // Save to Firestore
            await addDoc(collection(db, "usulanSeminar"), usulanData);

            setSuccess(`Usulan penjadwalan ulang ${selectedJenisSeminar === 'proposal' ? 'Seminar Proposal' :
              selectedJenisSeminar === 'hasil' ? 'Seminar Hasil' :
                'Sidang Komprehensif'} berhasil diajukan karena mahasiswa tidak lulus seminar sebelumnya.`);

            setShowSeminarForm(false);
            setSelectedMahasiswa(null);
            setSelectedJenisSeminar('');
            return;
          }
        }
      }

      // If this code is reached, it's a new seminar request (not a reschedule)
      // Data usulan
      const usulanData = {
        jenis: selectedJenisSeminar,
        tugasAkhirId: selectedMahasiswa.id,
        mahasiswaId: selectedMahasiswa.mahasiswaId,
        mahasiswaNama: selectedMahasiswa.mahasiswa?.nama || 'Mahasiswa',
        mahasiswaNIM: selectedMahasiswa.mahasiswa?.nim || '-',
        judulTA: selectedMahasiswa.judul || 'Tugas Akhir',
        pembimbing1Id: selectedMahasiswa.pembimbingId?.[0] || currentUser.uid,
        diajukanOleh: {
          dosen: currentUser.uid,
          nama: currentUser.displayName || 'Dosen',
          timestamp: new Date().toISOString()
        },
        status: 'pending_approval', // menunggu persetujuan admin
        createdAt: new Date().toISOString()
      };

      // Simpan ke Firestore (koleksi baru: usulanSeminar)
      await addDoc(collection(db, "usulanSeminar"), usulanData);

      setSuccess(`Usulan ${selectedJenisSeminar === 'proposal' ? 'Seminar Proposal' :
        selectedJenisSeminar === 'hasil' ? 'Seminar Hasil' :
          'Sidang Komprehensif'} berhasil diajukan.`);

      setShowSeminarForm(false);
      setSelectedMahasiswa(null);
      setSelectedJenisSeminar('');
    } catch (error) {
      console.error("Error submitting usulan seminar:", error);
      setError("Terjadi kesalahan saat mengajukan usulan seminar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title>Mahasiswa Bimbingan</Card.Title>
            <div>
              {pendingCount > 0 && (
                <Link to="/pending-assignments" className="me-2">
                  <Button variant="warning">
                    <FaUserCheck className="me-1" />
                    Penugasan Menunggu Konfirmasi ({pendingCount})
                  </Button>
                </Link>
              )}
              <Link to="/bimbingan">
                <Button variant="outline-primary">
                  <FaClipboardList className="me-1" />
                  Lihat Semua Bimbingan
                </Button>
              </Link>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          {pendingCount > 0 && (
            <Alert variant="warning" className="d-flex align-items-center">
              <FaInfoCircle className="me-2" size={20} />
              <div>
                Anda memiliki {pendingCount} penugasan pembimbing yang perlu dikonfirmasi.{' '}
                <Link to="/pending-assignments">Klik di sini</Link> untuk melihat dan mengkonfirmasi.
              </div>
            </Alert>
          )}

          {/* Pending reschedule requests */}
          {pendingRescheduleRequests.length > 0 && (
            <Alert variant="info" className="mt-3">
              <div className="d-flex align-items-center mb-3">
                <FaCalendarAlt className="me-2" size={20} />
                <h5 className="mb-0">Permintaan Penjadwalan Ulang Seminar ({pendingRescheduleRequests.length})</h5>
              </div>

              <Table responsive striped bordered className="mt-2">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Mahasiswa</th>
                    <th>Jenis Seminar</th>
                    <th>Tanggal Pengajuan</th>
                    <th>Alasan</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRescheduleRequests.map((request, index) => (
                    <tr key={request.id}>
                      <td>{index + 1}</td>
                      <td>
                        {request.mahasiswaNama || "Tidak ada nama"}<br />
                        <small>{request.mahasiswaNIM || "NIM tidak tersedia"}</small>
                      </td>
                      <td>
                        <Badge bg="info">
                          {request.jenis === 'proposal' ? 'Seminar Proposal' :
                            request.jenis === 'hasil' ? 'Seminar Hasil' :
                              'Sidang Komprehensif'}
                        </Badge>
                        {request.isReschedule && (
                          <Badge bg="warning" className="ms-1">Ulang</Badge>
                        )}
                      </td>
                      <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                      <td>
                        {request.diajukanOleh?.reason || "Nilai tidak mencapai 60"}
                        {request.previousSeminar && request.previousSeminar.nilai && (
                          <div className="mt-1">
                            <small>
                              <strong>Nilai sebelumnya:</strong> {(() => {
                                // Use standardized calculation function
                                const result = calculateSeminarScore(request.previousSeminar);
                                return result.finalScore.toFixed(2);
                              })()}
                            </small>
                          </div>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShowRescheduleRequest(request)}
                        >
                          Lihat Detail
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Alert>
          )}

          {/* Pending title change requests */}
          {pendingTitleChangeRequests.length > 0 && (
            <Alert variant="warning" className="mt-3">
              <div className="d-flex align-items-center mb-3">
                <h5 className="mb-0">Permintaan Perubahan Judul ({pendingTitleChangeRequests.length})</h5>
              </div>

              <Table responsive striped bordered className="mt-2">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Mahasiswa</th>
                    <th>Judul Lama</th>
                    <th>Judul Baru (Usulan)</th>
                    <th>Tanggal Pengajuan</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTitleChangeRequests.map((request, index) => (
                    <tr key={request.id}>
                      <td>{index + 1}</td>
                      <td>
                        {request.mahasiswa?.nama || "Tidak ada nama"}<br />
                        <small>{request.mahasiswa?.nim || "NIM tidak tersedia"}</small>
                      </td>
                      <td>
                        <small>{request.judul || request.usulanPerubahan?.previousJudul || '-'}</small>
                      </td>
                      <td>
                        <strong>{request.usulanPerubahan?.judul || '-'}</strong>
                      </td>
                      <td>{new Date(request.usulanPerubahan?.tanggal_usulan).toLocaleDateString()}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShowTitleChangeRequest(request)}
                        >
                          Lihat Detail
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Alert>
          )}

          {loading ? (
            <Alert variant="info">Memuat data mahasiswa bimbingan...</Alert>
          ) : mahasiswaList.length === 0 ? (
            <Alert variant="info">
              Belum ada mahasiswa bimbingan yang ditugaskan kepada Anda
              {pendingCount > 0 && ' atau Anda belum menerima penugasan yang ada'}
            </Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Mahasiswa</th>
                  <th>Judul Tugas Akhir</th>
                  <th>Status</th>
                  <th>Bimbingan</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {mahasiswaList.map((mahasiswa, index) => (
                  <tr key={mahasiswa.id}>
                    <td>{index + 1}</td>
                    <td>
                      {mahasiswa.mahasiswa ? (
                        <>
                          <div className="d-flex align-items-center">
                            <FaUser className="me-2 text-primary" />
                            <div>
                              <strong>{mahasiswa.mahasiswa.nama}</strong><br />
                              <small className="text-muted">{mahasiswa.mahasiswa.nim}</small>
                            </div>
                          </div>
                        </>
                      ) : (
                        <span className="text-muted">Data mahasiswa tidak ditemukan</span>
                      )}
                    </td>
                    <td>{mahasiswa.judul}</td>
                    <td>
                      {mahasiswa.acceptStatus === 'accepted' ? (
                        <Badge bg="success">Diterima</Badge>
                      ) : mahasiswa.acceptStatus === 'rejected' ? (
                        <Badge bg="danger">Ditolak</Badge>
                      ) : (
                        <Badge bg="warning">Menunggu Konfirmasi</Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-column">
                        <div className="mb-1">
                          <Badge bg="info">{mahasiswa.bimbinganCount} selesai</Badge>
                          {mahasiswa.pendingCount > 0 && (
                            <Badge bg="warning" className="ms-1">{mahasiswa.pendingCount} pending</Badge>
                          )}
                          <div>
                            <small className="text-muted">Total: {mahasiswa.totalBimbinganCount || 0} sesi</small>
                          </div>
                        </div>
                        <small>
                          Proposal: <Badge bg={mahasiswa.proposal >= 5 ? "success" : "secondary"} pill>{mahasiswa.proposal || 0}</Badge>
                          {mahasiswa.proposal >= 5 && !mahasiswa.completedSeminars?.proposal && !mahasiswa.scheduledSeminars?.proposal && (
                            <span className="ms-1 text-success">(Siap Seminar)</span>
                          )}
                          {mahasiswa.completedSeminars?.proposal && (
                            <span className="ms-1 text-muted">(Sudah Selesai)</span>
                          )}
                          {mahasiswa.scheduledSeminars?.proposal && !mahasiswa.completedSeminars?.proposal && (
                            <span className="ms-1 text-info">(Telah Diagendakan)</span>
                          )}
                        </small>
                        <small>
                          Hasil: <Badge bg={mahasiswa.hasil >= 5 ? "success" : "secondary"} pill>{mahasiswa.hasil || 0}</Badge>
                          {mahasiswa.hasil >= 5 && !mahasiswa.completedSeminars?.hasil && !mahasiswa.scheduledSeminars?.hasil && (
                            <span className="ms-1 text-success">(Siap Seminar)</span>
                          )}
                          {mahasiswa.completedSeminars?.hasil && (
                            <span className="ms-1 text-muted">(Sudah Selesai)</span>
                          )}
                          {mahasiswa.scheduledSeminars?.hasil && !mahasiswa.completedSeminars?.hasil && (
                            <span className="ms-1 text-info">(Telah Diagendakan)</span>
                          )}
                        </small>
                        <small>
                          Finalisasi: <Badge bg={mahasiswa.finalisasi >= 2 ? "success" : "secondary"} pill>{mahasiswa.finalisasi || 0}</Badge>
                          {mahasiswa.finalisasi >= 2 && !mahasiswa.completedSeminars?.kompre && !mahasiswa.scheduledSeminars?.kompre && (
                            <span className="ms-1 text-success">(Siap Sidang)</span>
                          )}
                          {mahasiswa.completedSeminars?.kompre && (
                            <span className="ms-1 text-muted">(Sudah Selesai)</span>
                          )}
                          {mahasiswa.scheduledSeminars?.kompre && !mahasiswa.completedSeminars?.kompre && (
                            <span className="ms-1 text-info">(Telah Diagendakan)</span>
                          )}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleViewBimbingan(mahasiswa)}
                        >
                          <FaClipboardList className="me-1" />
                          Lihat Bimbingan
                        </Button>

                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleGenerateLogbook(mahasiswa)}
                          disabled={loadingLogbook === mahasiswa.id}
                        >
                          <FaFileDownload className="me-1" />
                          {loadingLogbook === mahasiswa.id ? 'Generating...' : 'Generate Logbook'}
                        </Button>

                        {/* Cek apakah ini pembimbing 1 (P1) */}
                        {isPembimbing1(mahasiswa) && (
                          <>
                            {/* Force render the proposal button if count is >= 5, even if readyForProposal isn't set */}
                            {(mahasiswa.readyForProposal || mahasiswa.proposal >= 5) && (
                              <>
                                {/* Priority 1: Show status for completed seminars */}
                                {mahasiswa.completedSeminars?.proposal ? (
                                  <>
                                    <Button
                                      variant={mahasiswa.completedSeminars.proposal.isPassed ? "success" : "secondary"}
                                      size="sm"
                                      disabled
                                    >
                                      {mahasiswa.completedSeminars.proposal.isPassed ?
                                        "Seminar Proposal Sudah Lulus" :
                                        "Seminar Proposal Tidak Lulus"}
                                    </Button>
                                    {/* Show retry button if not passed and no scheduled */}
                                    {!mahasiswa.completedSeminars.proposal.isPassed && !mahasiswa.scheduledSeminars?.proposal && (
                                      <Button
                                        variant="warning"
                                        size="sm"
                                        onClick={() => handleAjukanSeminar(mahasiswa, 'proposal')}
                                        title="Ajukan seminar proposal ulang"
                                      >
                                        Ajukan Seminar Proposal Ulang
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {/* Priority 2: Show "Telah Diagendakan" if scheduled but not completed */}
                                    {mahasiswa.scheduledSeminars?.proposal ? (
                                      <div className="text-info small mb-1">
                                        <Badge bg="info">Seminar Proposal Telah Diagendakan</Badge>
                                      </div>
                                    ) : (
                                      /* Priority 3: Show button to submit new if not scheduled and not completed */
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleAjukanSeminar(mahasiswa, 'proposal')}
                                        title="Ajukan seminar proposal"
                                      >
                                        Ajukan Seminar Proposal
                                      </Button>
                                    )}
                                  </>
                                )}
                              </>
                            )}

                            {(mahasiswa.readyForHasil || mahasiswa.hasil >= 5) && (
                              <>
                                {/* Priority 1: Show status for completed seminars */}
                                {mahasiswa.completedSeminars?.hasil ? (
                                  <>
                                    <Button
                                      variant={mahasiswa.completedSeminars.hasil.isPassed ? "success" : "secondary"}
                                      size="sm"
                                      disabled
                                    >
                                      {mahasiswa.completedSeminars.hasil.isPassed ?
                                        "Seminar Hasil Sudah Lulus" :
                                        "Seminar Hasil Tidak Lulus"}
                                    </Button>
                                    {/* Show retry button if not passed and no scheduled */}
                                    {!mahasiswa.completedSeminars.hasil.isPassed && !mahasiswa.scheduledSeminars?.hasil && (
                                      <Button
                                        variant="warning"
                                        size="sm"
                                        onClick={() => handleAjukanSeminar(mahasiswa, 'hasil')}
                                        title="Ajukan seminar hasil ulang"
                                      >
                                        Ajukan Seminar Hasil Ulang
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {/* Priority 2: Show "Telah Diagendakan" if scheduled but not completed */}
                                    {mahasiswa.scheduledSeminars?.hasil ? (
                                      <div className="text-info small mb-1">
                                        <Badge bg="info">Seminar Hasil Telah Diagendakan</Badge>
                                      </div>
                                    ) : (
                                      /* Priority 3: Show button to submit new if not scheduled and not completed */
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleAjukanSeminar(mahasiswa, 'hasil')}
                                        title="Ajukan seminar hasil"
                                      >
                                        Ajukan Seminar Hasil
                                      </Button>
                                    )}
                                  </>
                                )}
                              </>
                            )}

                            {(mahasiswa.readyForSidang || mahasiswa.finalisasi >= 2) && (
                              <>
                                {/* Priority 1: Show status for completed seminars */}
                                {mahasiswa.completedSeminars?.kompre ? (
                                  <>
                                    <Button
                                      variant={mahasiswa.completedSeminars.kompre.isPassed ? "success" : "secondary"}
                                      size="sm"
                                      disabled
                                    >
                                      {mahasiswa.completedSeminars.kompre.isPassed ?
                                        "Sidang Kompre Sudah Lulus" :
                                        "Sidang Kompre Tidak Lulus"}
                                    </Button>
                                    {/* Show retry button if not passed and no scheduled */}
                                    {!mahasiswa.completedSeminars.kompre.isPassed && !mahasiswa.scheduledSeminars?.kompre && (
                                      <Button
                                        variant="warning"
                                        size="sm"
                                        onClick={() => handleAjukanSeminar(mahasiswa, 'kompre')}
                                        title="Ajukan sidang kompre ulang"
                                      >
                                        Ajukan Sidang Kompre Ulang
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {/* Priority 2: Show "Telah Diagendakan" if scheduled but not completed */}
                                    {mahasiswa.scheduledSeminars?.kompre ? (
                                      <div className="text-info small mb-1">
                                        <Badge bg="info">Sidang Kompre Telah Diagendakan</Badge>
                                      </div>
                                    ) : (
                                      /* Priority 3: Show button to submit new if not scheduled and not completed */
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleAjukanSeminar(mahasiswa, 'kompre')}
                                        title="Ajukan sidang komprehensif"
                                      >
                                        Ajukan Sidang Kompre
                                      </Button>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Form Usulan Seminar */}
      <Modal show={showSeminarForm} onHide={() => setShowSeminarForm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Ajukan {selectedJenisSeminar === 'proposal' ? 'Seminar Proposal' :
              selectedJenisSeminar === 'hasil' ? 'Seminar Hasil' :
                'Sidang Komprehensif'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          {selectedMahasiswa && (
            <div className="mb-3">
              <Alert variant="info">
                <p><strong>Mahasiswa:</strong> {selectedMahasiswa.mahasiswa?.nama}</p>
                <p><strong>NIM:</strong> {selectedMahasiswa.mahasiswa?.nim}</p>
                <p><strong>Judul:</strong> {selectedMahasiswa.judul}</p>

                <p className="mb-0">
                  <strong>Jumlah Bimbingan
                    {selectedJenisSeminar === 'proposal' ? ' Proposal' :
                      selectedJenisSeminar === 'hasil' ? ' Hasil' :
                        ' Finalisasi'}:</strong>{' '}
                  <Badge bg="primary">
                    {selectedJenisSeminar === 'proposal' ? selectedMahasiswa.proposal :
                      selectedJenisSeminar === 'hasil' ? selectedMahasiswa.hasil :
                        selectedMahasiswa.finalisasi} sesi
                  </Badge>
                </p>
              </Alert>

              {selectedMahasiswa.isReschedule && selectedMahasiswa.previousSeminar && (
                <Alert variant="warning">
                  <h6>Penjadwalan Ulang Seminar</h6>
                  <p>
                    Mahasiswa ini <strong>tidak lulus</strong> pada seminar sebelumnya.
                    Nilai sebelumnya: <strong>{selectedMahasiswa.previousSeminar.finalScore?.toFixed(2) || "Belum dinilai"}</strong>
                  </p>
                  <p className="mb-0">
                    Anda akan mengajukan penjadwalan ulang seminar untuk memberikan
                    kesempatan mahasiswa memperbaiki kekurangan pada seminar sebelumnya.
                  </p>
                </Alert>
              )}

              <p>
                Dengan mengajukan {selectedMahasiswa.isReschedule ? 'penjadwalan ulang ' : ''}
                {selectedJenisSeminar === 'proposal' ? 'seminar proposal' :
                  selectedJenisSeminar === 'hasil' ? 'seminar hasil' :
                    'sidang komprehensif'} ini, Anda menyatakan bahwa mahasiswa telah memenuhi semua persyaratan bimbingan dan siap untuk diuji.
              </p>

              <p>
                Pengajuan akan {selectedMahasiswa.isReschedule ? 'langsung' : ''} ditinjau oleh admin untuk penjadwalan seminar.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSeminarForm(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitUsulanSeminar}
            disabled={loading}
          >
            {loading ? 'Menyimpan...' : 'Ajukan Seminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Detail Reschedule Request */}
      <Modal show={showRescheduleModal} onHide={() => setShowRescheduleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Permintaan Penjadwalan Ulang {selectedRescheduleRequest?.jenis === 'proposal' ? 'Seminar Proposal' :
              selectedRescheduleRequest?.jenis === 'hasil' ? 'Seminar Hasil' :
                'Sidang Komprehensif'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          {selectedRescheduleRequest && (
            <div>
              <Alert variant="warning">
                <h6>Permintaan Penjadwalan Ulang</h6>
                <p>Mahasiswa ini <strong>tidak lulus</strong> pada seminar sebelumnya (nilai &lt; 60) dan meminta untuk dijadwalkan ulang.</p>
              </Alert>

              <div className="mb-4">
                <h6>Detail Mahasiswa:</h6>
                <p><strong>Nama:</strong> {selectedRescheduleRequest.mahasiswaNama || 'Tidak ada nama'}</p>
                <p><strong>NIM:</strong> {selectedRescheduleRequest.mahasiswaNIM || '-'}</p>
                <p><strong>Judul TA:</strong> {selectedRescheduleRequest.judulTA}</p>
                <p>
                  <strong>Jenis Seminar:</strong>{' '}
                  <Badge bg="info">
                    {selectedRescheduleRequest.jenis === 'proposal' ? 'Seminar Proposal' :
                      selectedRescheduleRequest.jenis === 'hasil' ? 'Seminar Hasil' :
                        'Sidang Komprehensif'}
                  </Badge>
                </p>
                <p><strong>Tanggal Pengajuan:</strong> {new Date(selectedRescheduleRequest.createdAt).toLocaleDateString()}</p>
                <p><strong>Alasan:</strong> {selectedRescheduleRequest.diajukanOleh?.reason || "Nilai tidak mencapai 60"}</p>
              </div>

              {selectedRescheduleRequest.previousSeminar && selectedRescheduleRequest.previousSeminar.nilai && (
                <div className="mb-4">
                  <h6>Detail Nilai Seminar Sebelumnya:</h6>
                  <Table responsive striped bordered>
                    <thead>
                      <tr>
                        <th>Penguji</th>
                        <th>Nilai</th>
                        <th>Bobot</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRescheduleRequest.previousSeminar.calculatedScores ? (
                        <>
                          {selectedRescheduleRequest.previousSeminar.calculatedScores.map((score, index) => (
                            <React.Fragment key={index}>
                              <tr>
                                <td>
                                  <strong>{score.evaluatorName}</strong>
                                  <Badge bg={
                                    score.role === "Pembimbing 1" ? "primary" :
                                      score.role === "Pembimbing 2" ? "secondary" :
                                        "info"
                                  } className="ms-2">{score.role}</Badge>
                                </td>
                                <td>{score.score.toFixed(2)}</td>
                                <td>{(score.weight * 100).toFixed(0)}%</td>
                              </tr>
                              <tr>
                                <td colSpan="3" className="p-0">
                                  <div className="bg-light p-2 border-top border-bottom" style={{ fontSize: '0.9em' }}>
                                    <strong>Rincian penilaian:</strong>
                                    <Table size="sm" className="mb-0 mt-1">
                                      <thead>
                                        <tr>
                                          <th>Kriteria</th>
                                          <th>Nilai</th>
                                          <th>Bobot</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {score.criteriaScores.map((cs, i) => (
                                          <tr key={i}>
                                            <td>{cs.name}</td>
                                            <td>{cs.score}</td>
                                            <td>{cs.weight}%</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </td>
                              </tr>
                            </React.Fragment>
                          ))}
                          <tr className="table-primary">
                            <td colSpan="2"><strong>Nilai Akhir</strong></td>
                            <td>
                              <strong>{selectedRescheduleRequest.previousSeminar.finalScore.toFixed(2)}</strong>
                              <Badge bg={selectedRescheduleRequest.previousSeminar.isPassed ? "success" : "danger"} className="ms-2">
                                {selectedRescheduleRequest.previousSeminar.isPassed ? "LULUS" : "TIDAK LULUS"}
                              </Badge>
                            </td>
                          </tr>
                        </>
                      ) : (() => {
                        // Use standardized calculation from seminarUtils
                        const seminar = selectedRescheduleRequest.previousSeminar;
                        const result = calculateSeminarScore(seminar);
                        const finalScore = result.finalScore;
                        const scores = result.evaluatorScores;

                        // Get penguji names for display
                        const pengujiNama = {};
                        if (Array.isArray(seminar.pengujiId) && Array.isArray(seminar.pengujiNama)) {
                          seminar.pengujiId.forEach((id, idx) => {
                            pengujiNama[id] = seminar.pengujiNama[idx] || `Penguji ${idx + 1}`;
                          });
                        }

                        // Prepare scores display structure
                        const scoresWithDetails = scores.map(score => {
                          // Get evaluator name
                          const name = pengujiNama[score.id] ||
                            getEvaluatorName(seminar, score.id, score.role);

                          // Get criteria details
                          const criteriaScores = [];
                          const pengujiScore = seminar.nilai[score.id];

                          if (seminar.kriteriaPenilaian && Array.isArray(seminar.kriteriaPenilaian)) {
                            seminar.kriteriaPenilaian.forEach(kriteria => {
                              if (kriteria && kriteria.id && pengujiScore[kriteria.id]) {
                                const nilai = pengujiScore[kriteria.id];
                                const bobot = kriteria.bobot / 100;
                                const weightedScore = nilai * bobot;

                                criteriaScores.push({
                                  name: kriteria.nama,
                                  weight: kriteria.bobot,
                                  score: nilai,
                                  weighted: weightedScore
                                });
                              }
                            });
                          }

                          return {
                            id: score.id,
                            name: name,
                            role: score.role,
                            score: score.score,
                            weight: score.weight,
                            criteriaScores: criteriaScores
                          };
                        });

                        return (
                          <>
                            {scoresWithDetails.map((score, index) => (
                              <React.Fragment key={index}>
                                <tr>
                                  <td>
                                    <strong>{score.name}</strong>
                                    <Badge bg={
                                      score.role === "Pembimbing 1" ? "primary" :
                                        score.role === "Pembimbing 2" ? "secondary" :
                                          "info"
                                    } className="ms-2">{score.role}</Badge>
                                  </td>
                                  <td>{score.score.toFixed(2)}</td>
                                  <td>{(score.weight * 100).toFixed(0)}%</td>
                                </tr>
                                <tr>
                                  <td colSpan="3" className="p-0">
                                    <div className="bg-light p-2 border-top border-bottom" style={{ fontSize: '0.9em' }}>
                                      <strong>Rincian penilaian:</strong>
                                      <Table size="sm" className="mb-0 mt-1">
                                        <thead>
                                          <tr>
                                            <th>Kriteria</th>
                                            <th>Nilai</th>
                                            <th>Bobot</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {score.criteriaScores.map((cs, i) => (
                                            <tr key={i}>
                                              <td>{cs.name}</td>
                                              <td>{cs.score}</td>
                                              <td>{cs.weight}%</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                            <tr className="table-primary">
                              <td colSpan="2"><strong>Nilai Akhir</strong></td>
                              <td><strong>{finalScore.toFixed(2)}</strong> <Badge bg={result.isPassed ? "success" : "danger"}>{result.isPassed ? "LULUS" : "TIDAK LULUS"}</Badge></td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </Table>
                </div>
              )}

              <Alert variant="info">
                <div className="mb-0">
                  <strong>Status:</strong> Menunggu persetujuan Anda (P1)
                  <div className="mt-2">
                    <span className="me-2">Alur Persetujuan:</span>
                    <Badge bg="success">Mahasiswa</Badge>
                    <FaArrowRight className="mx-1" />
                    <Badge bg="warning">Pembimbing (P1)</Badge>
                    <FaArrowRight className="mx-1" />
                    <Badge bg="secondary">Admin</Badge>
                  </div>
                  <div className="mt-2">
                    Sebagai Pembimbing 1, Anda perlu menyetujui atau menolak permintaan penjadwalan ulang ini.
                    Jika disetujui, permintaan akan diteruskan ke Admin untuk penjadwalan.
                  </div>
                </div>
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="container-fluid">
            <div className="row mb-3">
              <div className="col-12">
                <Form.Group>
                  <Form.Label>Alasan Penolakan (opsional, diisi jika ingin menolak)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Berikan alasan mengapa permintaan penjadwalan ulang ditolak"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>
            <div className="row">
              <div className="col-12 d-flex justify-content-between">
                <Button variant="secondary" onClick={() => setShowRescheduleModal(false)}>
                  Tutup
                </Button>
                <div>
                  <Button
                    variant="danger"
                    onClick={showRejectConfirmation}
                    disabled={loading}
                    className="me-2"
                  >
                    Tolak Permintaan
                  </Button>
                  <Button
                    variant="success"
                    onClick={showApprovalConfirmation}
                    disabled={loading}
                  >
                    Setujui Permintaan
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal Konfirmasi Penolakan */}
      <Modal show={showRejectConfirm} onHide={closeRejectConfirmation}>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Penolakan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <p>Anda yakin ingin menolak permintaan penjadwalan ulang seminar ini?</p>
            <p>Mahasiswa perlu mengajukan permintaan baru jika permintaan ini ditolak.</p>
            {rejectReason ? (
              <div className="mt-2">
                <strong>Alasan penolakan:</strong>
                <p className="mt-1">{rejectReason}</p>
              </div>
            ) : (
              <p className="mt-2 text-danger">Anda belum memberikan alasan penolakan. Alasan penolakan sangat membantu mahasiswa untuk mengetahui penyebab permintaan ditolak.</p>
            )}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRejectConfirmation}>
            Kembali
          </Button>
          <Button variant="danger" onClick={handleRejectReschedule} disabled={loading}>
            {loading ? 'Memproses...' : 'Ya, Tolak Permintaan'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Konfirmasi Penolakan */}
      <Modal show={showRejectConfirm} onHide={closeRejectConfirmation}>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Penolakan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <p>Anda yakin ingin menolak permintaan penjadwalan ulang seminar ini?</p>
            <p>Mahasiswa perlu mengajukan permintaan baru jika permintaan ini ditolak.</p>
            {rejectReason ? (
              <div className="mt-2">
                <strong>Alasan penolakan:</strong>
                <p className="mt-1">{rejectReason}</p>
              </div>
            ) : (
              <p className="mt-2 text-danger">Anda belum memberikan alasan penolakan. Alasan penolakan sangat membantu mahasiswa untuk mengetahui penyebab permintaan ditolak.</p>
            )}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRejectConfirmation}>
            Kembali
          </Button>
          <Button variant="danger" onClick={handleRejectReschedule} disabled={loading}>
            {loading ? 'Memproses...' : 'Ya, Tolak Permintaan'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Konfirmasi Persetujuan */}
      <Modal show={showApproveConfirm} onHide={closeApprovalConfirmation}>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Persetujuan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success">
            <p>Anda akan menyetujui permintaan penjadwalan ulang seminar ini.</p>
            <p>Setelah disetujui, permintaan akan diteruskan ke Admin untuk penjadwalan.</p>

            {selectedRescheduleRequest && (
              <div className="mt-3">
                <p><strong>Detail Permintaan:</strong></p>
                <ul>
                  <li>Mahasiswa: {selectedRescheduleRequest.mahasiswaNama}</li>
                  <li>Jenis Seminar: {selectedRescheduleRequest.jenis === 'proposal' ? 'Seminar Proposal' :
                    selectedRescheduleRequest.jenis === 'hasil' ? 'Seminar Hasil' :
                      'Sidang Komprehensif'}</li>
                  <li>Nilai Sebelumnya: {
                    selectedRescheduleRequest.previousSeminar?.finalScore?.toFixed(2) ||
                    (selectedRescheduleRequest.previousSeminar?.nilai ? 'Kurang dari 60' : 'Belum dinilai')
                  }</li>
                </ul>
              </div>
            )}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeApprovalConfirmation}>
            Kembali
          </Button>
          <Button variant="success" onClick={handleApproveReschedule} disabled={loading}>
            {loading ? 'Memproses...' : 'Ya, Setujui Permintaan'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Detail Permintaan Perubahan Judul */}
      <Modal show={showTitleChangeModal} onHide={() => setShowTitleChangeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Permintaan Perubahan Judul</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTitleChangeRequest && (
            <div>
              <Alert variant="info">
                <p>Mahasiswa meminta perubahan judul tugas akhir. Silakan tinjau dan berikan persetujuan atau penolakan.</p>
              </Alert>

              <div className="mb-3">
                <p><strong>Nama:</strong> {selectedTitleChangeRequest.mahasiswa?.nama || 'Tidak ada nama'}</p>
                <p><strong>NIM:</strong> {selectedTitleChangeRequest.mahasiswa?.nim || '-'}</p>
                <p><strong>Judul Saat Ini:</strong> {selectedTitleChangeRequest.judul || selectedTitleChangeRequest.usulanPerubahan?.previousJudul || '-'}</p>
                <p><strong>Judul Baru (Usulan):</strong>
                  <strong className="text-primary"> {selectedTitleChangeRequest.usulanPerubahan?.judul || '-'}</strong>
                </p>
                <p><strong>Link Dokumen:</strong>
                  {selectedTitleChangeRequest.usulanPerubahan?.driveLink ? (
                    <a href={selectedTitleChangeRequest.usulanPerubahan.driveLink} target="_blank" rel="noopener noreferrer" className="ms-2">
                      {selectedTitleChangeRequest.usulanPerubahan.driveLink}
                    </a>
                  ) : '-'}
                </p>
                <p><strong>Tanggal Pengajuan:</strong> {
                  selectedTitleChangeRequest.usulanPerubahan?.tanggal_usulan ?
                    new Date(selectedTitleChangeRequest.usulanPerubahan.tanggal_usulan).toLocaleString('id-ID') : '-'
                }</p>
              </div>

              <Alert variant="warning">
                <strong>Catatan:</strong> Setelah Anda menyetujui, judul akan langsung diupdate tanpa perlu persetujuan Admin lagi.
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTitleChangeModal(false)}>
            Batal
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowTitleChangeRejectConfirm(true)}
            disabled={loading}
          >
            Tolak
          </Button>
          <Button
            variant="success"
            onClick={handleApproveTitleChange}
            disabled={loading}
          >
            {loading ? 'Memproses...' : 'Setujui & Update Judul'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Konfirmasi Penolakan Perubahan Judul */}
      <Modal show={showTitleChangeRejectConfirm} onHide={() => setShowTitleChangeRejectConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Tolak Permintaan Perubahan Judul</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <p>Anda akan menolak permintaan perubahan judul ini.</p>
            {selectedTitleChangeRequest && (
              <div className="mt-3">
                <p><strong>Mahasiswa:</strong> {selectedTitleChangeRequest.mahasiswa?.nama}</p>
                <p><strong>Judul Baru (Usulan):</strong> {selectedTitleChangeRequest.usulanPerubahan?.judul}</p>
              </div>
            )}
          </Alert>

          <Form.Group className="mt-3">
            <Form.Label>Alasan Penolakan (Opsional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={titleChangeRejectReason}
              onChange={(e) => setTitleChangeRejectReason(e.target.value)}
              placeholder="Berikan alasan penolakan permintaan perubahan judul (opsional)"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTitleChangeRejectConfirm(false)}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleRejectTitleChange} disabled={loading}>
            {loading ? 'Memproses...' : 'Ya, Tolak Permintaan'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MahasiswaBimbingan;